import {
  Children,
  Fragment,
  ReactElement,
  ReactNode,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useId,
  useState
} from "react";
import { createPortal } from "react-dom";
import { HeadManager, MANAGED_HEAD_ATTRIBUTE } from "./head-manager";

type HeadProviderProps = {
  manager: HeadManager;
  children: ReactNode;
};

type HeadProps = {
  children: ReactNode;
};

const HeadContext = createContext<HeadManager | null>(null);

const markServerNode = (node: ReactNode): ReactNode => {
  if (!isValidElement<{ children?: ReactNode }>(node)) return node;

  if (node.type === Fragment) {
    return cloneElement(
      node,
      undefined,
      Children.map(node.props.children, markServerNode)
    );
  }

  return cloneElement(node as ReactElement<any>, { [MANAGED_HEAD_ATTRIBUTE]: "true" });
};

const removeServerHeadNodes = () => {
  document.head
    .querySelectorAll(`[${MANAGED_HEAD_ATTRIBUTE}]`)
    .forEach(node => node.remove());
};

export const HeadProvider = ({ manager, children }: HeadProviderProps) =>
  <HeadContext.Provider value={manager}>{children}</HeadContext.Provider>;

export const Head = ({ children }: HeadProps) => {
  const key = useId();
  const manager = useContext(HeadContext);
  const [ portalReady, setPortalReady ] = useState(false);

  if (typeof document === "undefined") {
    manager?.upsert(key, Children.toArray(children).map(markServerNode));
  }

  useEffect(() => {
    removeServerHeadNodes();
    setPortalReady(true);
  }, []);

  if (typeof document === "undefined" || !portalReady) return null;

  return createPortal(<>{children}</>, document.head);
};
